/**
 * APR 02 (Kaleigh Vazquez & Josie Evans, "PA Sale to Approval - IF THIS THEN THAT", 2026-10-08, Process 1 Step 5):
 * Josie checks FLMMIS 2-3 times a week while a case waits for FLMMIS to reflect a correction or resubmission.
 *
 * The row's `flmmis_check` is the server's answer (readModel FLMMIS_CHECK_ROW): whether the case is waiting, the
 * Florida day FLMMIS was last checked, and whether a check is due. This marker only words it; it offers no action of
 * its own. A check is recorded with the approval review (FLMMIS not reflected, continue) and clears the marker.
 */
import React, { useState } from 'react';

export interface FlmmisCheck {
  waiting: boolean;
  /** YYYY-MM-DD (Florida day), or null when no check is recorded. */
  last_checked_on: string | null;
  due: boolean;
  /** YYYY-MM-DD the check became due, or null (never checked). */
  due_since: string | null;
}

export interface FlmmisCheckRow {
  id: string;
  flmmis_check?: FlmmisCheck | null;
}

/** A plain calendar date, never parsed through the browser's zone (so the Florida day does not slip). */
export function checkDate(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m[2]) - 1] || m[2]} ${Number(m[3])}`;
}

export const FLMMIS_CHECK_EXPLAIN = 'This case is waiting for FLMMIS to reflect a correction or resubmission. '
  + 'Check FLMMIS, then record the check with the approval review (FLMMIS not reflected, continue), or record it reflected.';

export function flmmisCheckLine(check: FlmmisCheck): string {
  const last = check.last_checked_on ? `last ${checkDate(check.last_checked_on)}` : 'never checked';
  return check.due ? `FLMMIS check due · ${last}` : `FLMMIS checked ${checkDate(check.last_checked_on)}`;
}

/** Today on the Florida calendar (America/New_York), as YYYY-MM-DD: the day the check is dated, whatever the browser's zone. */
export function floridaToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/**
 * `onCheck` is given only where the server accepts the bare check: an APPROVED - SFS CORRECTION case whose correction is
 * still open (PAR 02-03). It posts the existing approval review (FLMMIS not reflected, continue) dated `on`, and answers
 * null when it was recorded or the words of the refusal. REFLECTED is never recorded here: that is the completion.
 */
export function FlmmisCheckMarker({ row, onCheck }: { row: FlmmisCheckRow; onCheck?: (on: string) => Promise<string | null> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const check = row.flmmis_check;
  if (!check || !check.waiting) return null;
  const record = async () => {
    setBusy(true); setErr(null);
    try { setErr(await onCheck!(floridaToday())); } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <span className="um-sub" data-testid="flmmis-check">
      <span className={check.due ? 'um-tag attn' : 'um-tag'} title={FLMMIS_CHECK_EXPLAIN}>
        {flmmisCheckLine(check)}
        <span className="um-sr-only">. {FLMMIS_CHECK_EXPLAIN}</span>
      </span>
      {onCheck && (
        <button type="button" className="um-link" disabled={busy} data-testid="flmmis-check-record"
          title="Records an approval review: FLMMIS checked today, not yet reflected. Nothing else changes."
          onClick={(e) => { e.stopPropagation(); void record(); }}>
          FLMMIS checked · not yet
        </button>
      )}
      {err && <span className="um-err" role="alert">{err}</span>}
    </span>
  );
}

export default FlmmisCheckMarker;
